/**
 * The Driver App's outbox drain (EP10-S15, `10/04` §3, §5, §9).
 *
 * Every action at the door is a command written to a durable outbox first;
 * this module sends the outbox to `POST /sync/push` in capture order and
 * applies what the server answered. It knows nothing about IndexedDB or the
 * network: the store and the push are handed in, which is what lets the app
 * keep the outbox in IndexedDB and a test keep it in an array.
 */
import type { SyncMutationInput, SyncMutationResult } from "./client";

/** An entry is queued until the server answers it; conflicts and rejections stay for the record. */
export type OutboxState = "queued" | "conflict" | "rejected";

export interface OutboxEntry extends SyncMutationInput {
	/** Capture order. The drain sends entries sorted by it, never by the clock. */
	seq: number;
	state: OutboxState;
	/** How many pushes answered `retry` for it. */
	attempts: number;
	/** The conflict or rejection reason, once there is one. */
	reason?: string;
}

export interface OutboxStore {
	all(): Promise<OutboxEntry[]>;
	put(entry: OutboxEntry): Promise<void>;
	remove(idempotencyKey: string): Promise<void>;
}

/** Sends one batch; resolves with one result per mutation, in order. */
export type Push = (mutations: SyncMutationInput[]) => Promise<SyncMutationResult[]>;

export interface DrainResult {
	/** Applied now, or already applied before (a duplicate). */
	applied: number;
	/** Held by the server for a dispatcher: conflicts and rejections. */
	held: number;
	/** The server asked to try again later; the rest stays queued. */
	retry: boolean;
}

/** The server's batch limit (`10/04` §8). */
export const BATCH = 50;

function input({
	idempotencyKey,
	command,
	shipmentId,
	payload,
	capturedAt,
}: OutboxEntry): SyncMutationInput {
	return shipmentId === undefined
		? { idempotencyKey, command, payload, capturedAt }
		: { idempotencyKey, command, shipmentId, payload, capturedAt };
}

/**
 * Drains the queued entries in capture order, fifty at a time.
 *
 * Applied and duplicate entries leave the outbox. Conflicts and rejections
 * are kept, marked, so the sync list can show them — the server holds them
 * for a dispatcher and nothing is lost either way. The first `retry` stops
 * the drain with that entry and everything after it still queued, so the
 * order a driver worked in is the order the server sees. A network failure
 * rejects, and the caller backs off.
 */
export async function drain(store: OutboxStore, push: Push): Promise<DrainResult> {
	const out: DrainResult = { applied: 0, held: 0, retry: false };
	for (;;) {
		const queued = (await store.all())
			.filter((e) => e.state === "queued")
			.sort((a, b) => a.seq - b.seq);
		const batch = queued.slice(0, BATCH);
		if (batch.length === 0) {
			return out;
		}
		const results = await push(batch.map(input));
		for (const [i, entry] of batch.entries()) {
			const result = results[i];
			if (result === undefined || result.status === "retry") {
				await store.put({ ...entry, attempts: entry.attempts + 1 });
				out.retry = true;
				return out;
			}
			if (result.status === "applied" || result.status === "duplicate") {
				await store.remove(entry.idempotencyKey);
				out.applied++;
				continue;
			}
			await store.put({
				...entry,
				state: result.status,
				...(result.reason === undefined ? {} : { reason: result.reason }),
			});
			out.held++;
		}
	}
}

/** Exponential backoff: 1 s, 2 s, 4 s … capped at five minutes. */
export function backoff(failures: number): number {
	return Math.min(1000 * 2 ** Math.max(0, failures - 1), 5 * 60_000);
}

/**
 * How long after a successful sync the next one runs on its own.
 *
 * The realtime stream (EP10-S22) already syncs at once on a shipment hint;
 * this is the floor for when the stream is down or a hint is missed.
 */
export const POLL_MS = 30_000;

export interface SyncLoop {
	/** Runs a sync now, or right after the one in flight. */
	trigger(): void;
	stop(): void;
}

/**
 * Runs `cycle` on start, when the window regains focus, when the browser
 * comes back online, whenever `trigger` is called, and {@link POLL_MS} after
 * the last success — so a stop dispatch adds shows within a minute with the
 * app left open (EP10-S16). A cycle that throws is retried with
 * {@link backoff}; one that succeeds resets it. Cycles never overlap: a
 * trigger during one runs once more after it.
 */
export function startSyncLoop(cycle: () => Promise<void>, target: Window = window): SyncLoop {
	let failures = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;
	let running = false;
	let again = false;
	let stopped = false;

	async function tick(): Promise<void> {
		if (stopped) {
			return;
		}
		if (running) {
			again = true;
			return;
		}
		running = true;
		clearTimeout(timer);
		try {
			await cycle();
			failures = 0;
			timer = setTimeout(() => void tick(), POLL_MS);
		} catch {
			failures++;
			timer = setTimeout(() => void tick(), backoff(failures));
		} finally {
			running = false;
			if (again) {
				again = false;
				void tick();
			}
		}
	}

	const onEvent = () => void tick();
	target.addEventListener("focus", onEvent);
	target.addEventListener("online", onEvent);
	void tick();

	return {
		trigger: onEvent,
		stop() {
			stopped = true;
			clearTimeout(timer);
			target.removeEventListener("focus", onEvent);
			target.removeEventListener("online", onEvent);
		},
	};
}
